import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { User, Settings, CreditCard, Moon, Sun, LogOut, KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountPage() {
    const navigate = useNavigate();
    const { user, settings, updateSettings, logout, isSubscribed, getAuthHeaders } = useAuth();
    const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
    const [pwLoading, setPwLoading] = useState(false);

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (pwForm.new_password !== pwForm.confirm) {
            toast.error('Nová hesla se neshodují');
            return;
        }
        if (pwForm.new_password.length < 8) {
            toast.error('Heslo musí mít alespoň 8 znaků');
            return;
        }
        setPwLoading(true);
        try {
            await axios.put(`${API}/user/password`, {
                current_password: pwForm.current_password,
                new_password: pwForm.new_password,
            }, { headers: getAuthHeaders() });
            toast.success('Heslo bylo úspěšně změněno');
            setPwForm({ current_password: '', new_password: '', confirm: '' });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Nepodařilo se změnit heslo');
        } finally {
            setPwLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
        toast.success('Odhlášení proběhlo úspěšně');
    };

    const getSubscriptionStatus = () => {
        switch (user?.subscription_status) {
            case 'active':
                return { label: 'Aktivní', color: 'text-green-600 dark:text-green-400' };
            case 'trialing':
                return { label: 'Zkušební období', color: 'text-amber-600 dark:text-amber-400' };
            case 'past_due':
                return { label: 'Nezaplaceno', color: 'text-red-600 dark:text-red-400' };
            case 'canceled':
                return { label: 'Zrušeno', color: 'text-muted-foreground' };
            default:
                return { label: 'Neaktivní', color: 'text-muted-foreground' };
        }
    };

    const status = getSubscriptionStatus();

    return (
        <div className="min-h-screen" data-testid="account-page">
            <section className="listarna-section">
                <div className="listarna-container">
                    <div className="max-w-2xl mx-auto">
                        {/* Header */}
                        <div className="mb-12">
                            <h1 className="font-heading text-3xl md:text-4xl font-semibold mb-2">
                                Můj účet
                            </h1>
                            <p className="text-muted-foreground">
                                Spravujte své nastavení a předplatné
                            </p>
                        </div>

                        {/* Profile Section */}
                        <div className="listarna-card rounded-lg p-6 mb-6">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
                                    <User className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <div>
                                    <h2 className="font-heading text-xl font-medium">{user?.name}</h2>
                                    <p className="text-muted-foreground">{user?.email}</p>
                                </div>
                            </div>
                            
                            <div className="flex items-center justify-between pt-4 border-t border-border">
                                <span className="text-muted-foreground">Role</span>
                                <span className="capitalize">{user?.role === 'admin' ? 'Administrátor' : 'Čtenář'}</span>
                            </div>
                        </div>

                        {/* Subscription Section */}
                        <div className="listarna-card rounded-lg p-6 mb-6">
                            <div className="flex items-center gap-3 mb-6">
                                <CreditCard className="w-5 h-5" />
                                <h2 className="font-heading text-xl font-medium">Předplatné</h2>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Stav</span>
                                    <span className={status.color}>{status.label}</span>
                                </div>

                                {user?.subscription_end && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground">Platné do</span>
                                        <span>{new Date(user.subscription_end).toLocaleDateString('cs-CZ')}</span>
                                    </div>
                                )}

                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Cena</span>
                                    <span>149 Kč/měsíc</span>
                                </div>
                            </div>

                            <div className="mt-6 pt-6 border-t border-border">
                                {isSubscribed() ? (
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Správa předplatného bude dostupná po integraci platební brány.
                                    </p>
                                ) : (
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            Aktivujte předplatné a získejte přístup ke všem povídkám.
                                            První měsíc zdarma!
                                        </p>
                                        <Button 
                                            className="listarna-btn-primary w-full"
                                            disabled
                                            data-testid="activate-subscription-btn"
                                        >
                                            Aktivovat předplatné (připravujeme)
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Reading Settings Section */}
                        <div className="listarna-card rounded-lg p-6 mb-6">
                            <div className="flex items-center gap-3 mb-6">
                                <Settings className="w-5 h-5" />
                                <h2 className="font-heading text-xl font-medium">Nastavení čtení</h2>
                            </div>

                            <div className="space-y-6">
                                {/* Font Family */}
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground mb-2 block">
                                        Písmo
                                    </label>
                                    <Select 
                                        value={settings.font_family}
                                        onValueChange={(value) => updateSettings({ ...settings, font_family: value })}
                                    >
                                        <SelectTrigger data-testid="account-font-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Cormorant Garamond">Cormorant Garamond</SelectItem>
                                            <SelectItem value="Merriweather">Merriweather</SelectItem>
                                            <SelectItem value="Spectral">Spectral</SelectItem>
                                            <SelectItem value="Proza Libre">Proza Libre</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Font Size */}
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground mb-2 block">
                                        Velikost písma: {settings.font_size}px
                                    </label>
                                    <Slider
                                        value={[settings.font_size]}
                                        onValueChange={(value) => updateSettings({ ...settings, font_size: value[0] })}
                                        min={14}
                                        max={28}
                                        step={1}
                                        data-testid="account-font-size-slider"
                                    />
                                </div>

                                {/* Line Height */}
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground mb-2 block">
                                        Řádkování: {settings.line_height}
                                    </label>
                                    <Slider
                                        value={[settings.line_height]}
                                        onValueChange={(value) => updateSettings({ ...settings, line_height: value[0] })}
                                        min={1.4}
                                        max={2.4}
                                        step={0.1}
                                        data-testid="account-line-height-slider"
                                    />
                                </div>

                                {/* Dark Mode */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {settings.dark_mode ? (
                                            <Moon className="w-5 h-5" />
                                        ) : (
                                            <Sun className="w-5 h-5" />
                                        )}
                                        <span className="font-medium">Tmavý režim</span>
                                    </div>
                                    <button
                                        onClick={() => updateSettings({ ...settings, dark_mode: !settings.dark_mode })}
                                        className={`relative w-12 h-6 rounded-full transition-colors ${
                                            settings.dark_mode ? 'bg-primary' : 'bg-secondary'
                                        }`}
                                        data-testid="account-dark-mode-switch"
                                        aria-label="Toggle dark mode"
                                    >
                                        <span className={`absolute top-1 w-4 h-4 rounded-full bg-background transition-transform ${
                                            settings.dark_mode ? 'translate-x-7' : 'translate-x-1'
                                        }`} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Change Password Section */}
                        <div className="listarna-card rounded-lg p-6 mb-6">
                            <div className="flex items-center gap-3 mb-6">
                                <KeyRound className="w-5 h-5" />
                                <h2 className="font-heading text-xl font-medium">Změna hesla</h2>
                            </div>
                            <form onSubmit={handlePasswordChange} className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground mb-1 block">
                                        Současné heslo
                                    </label>
                                    <input
                                        type="password"
                                        className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                                        value={pwForm.current_password}
                                        onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))}
                                        required
                                        autoComplete="current-password"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground mb-1 block">
                                        Nové heslo
                                    </label>
                                    <input
                                        type="password"
                                        className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                                        value={pwForm.new_password}
                                        onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))}
                                        required
                                        minLength={8}
                                        autoComplete="new-password"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground mb-1 block">
                                        Potvrzení nového hesla
                                    </label>
                                    <input
                                        type="password"
                                        className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                                        value={pwForm.confirm}
                                        onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                                        required
                                        minLength={8}
                                        autoComplete="new-password"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="listarna-btn-primary w-full"
                                    disabled={pwLoading}
                                >
                                    {pwLoading ? 'Ukládám...' : 'Změnit heslo'}
                                </Button>
                            </form>
                        </div>

                        {/* Logout */}
                        <Button
                            variant="outline"
                            className="w-full flex items-center justify-center gap-2"
                            onClick={handleLogout}
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4" />
                            Odhlásit se
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
}
